import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import React, { useEffect, useMemo, useState } from 'react';

import { ManifestSection } from './ManifestSection';
import { NodeSearchField } from './NodeSearchField';
import { ShipmentFormActions, ShipmentSubmitAction } from './ShipmentFormActions';
import { ShipmentReferenceFields } from './ShipmentReferenceFields';
import { useDestinationSuggestions } from './useDestinationSuggestions';
import { useManifest } from './useManifest';
import { useShipmentSubmit } from './useShipmentSubmit';
import { ActionPermissions, CustomPermission } from '../../../../../@types';
import { LocationNodeData } from '../../../../../@types/live-search.type';
import { Shipment } from '../../../../../@types/shipment.type';
import { isUserHasPermission } from '../../../../../configs/permissions';
import { useGetLoggedInSiblingParentDetails } from '../../../../../queries/structure-query';

export interface CreateShipmentFormProps {
  serviceId: number;
  destinationNodeId?: number;
  destinationName?: string;
  mainLocationNodeId?: number;
  subLocationNodeId?: number;
  primaryAllocationNodeId?: number;
  secondaryAllocationNodeId?: number;
  clientHierarchy?: string;
  clientContractIds?: number[];
  clientName?: string;
  contractName?: string;
  permissions?: CustomPermission[];
  onCreated: (shipment: Shipment, action: ShipmentSubmitAction) => void;
  onCancel: () => void;
}

export const CreateShipmentForm: React.FC<CreateShipmentFormProps> = ({
  serviceId,
  destinationNodeId,
  destinationName,
  mainLocationNodeId,
  subLocationNodeId,
  primaryAllocationNodeId,
  secondaryAllocationNodeId,
  clientHierarchy,
  clientContractIds,
  clientName,
  contractName,
  permissions,
  onCreated,
  onCancel,
}) => {
  const initialDestination = useMemo<LocationNodeData | null>(
    () =>
      destinationNodeId != null && destinationName
        ? ({ id: destinationNodeId, name: destinationName } as LocationNodeData)
        : null,
    [destinationNodeId, destinationName]
  );

  const initialDestinationInputValue = useMemo<string | undefined>(
    () => (destinationNodeId == null && destinationName ? destinationName : undefined),
    [destinationNodeId, destinationName]
  );

  const canDispatch = isUserHasPermission(ActionPermissions.Shipment_View_Dispatch, permissions);

  const [source, setSource] = useState<LocationNodeData | null>(null);
  const [autoSource, setAutoSource] = useState<LocationNodeData | null>(null);
  const [destination, setDestination] = useState<LocationNodeData | null>(initialDestination);
  const [note, setNote] = useState('');
  const [ref1, setRef1] = useState('');
  const [bypassReceipt, setBypassReceipt] = useState(false);

  const parentNodeQuery = useGetLoggedInSiblingParentDetails();
  const destinationSuggestions = useDestinationSuggestions({
    serviceId,
    mainLocationNodeId,
    subLocationNodeId,
    primaryAllocationNodeId,
    secondaryAllocationNodeId,
  });
  const manifestState = useManifest({ serviceId, clientContractIds, source });
  const submitState = useShipmentSubmit({
    serviceId,
    canDispatch,
    source,
    destination,
    clientHierarchy,
    clientName,
    contractName,
    note,
    ref1,
    bypassReceipt,
    manifest: manifestState.manifest,
    onCreated,
  });

  useEffect(() => {
    parentNodeQuery.refetch();
  }, []);

  useEffect(() => {
    const data = parentNodeQuery.data;
    if (!data) return;
    const id = data.parentDetails?.id ?? data.siblingDetails?.id;
    const name = data.parentDetails?.name ?? data.siblingDetails?.name;
    if (id && name) {
      const node = { id, name } as LocationNodeData;
      setAutoSource(node);
      setSource((prev) => prev ?? node);
    }
  }, [parentNodeQuery.data]);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'v4.border',
        borderRadius: 2,
        p: 2,
        mt: 1,
        bgcolor: 'v4.background',
      }}
    >
      <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1.5 }}>
        Create New Shipment
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          columnGap: 1.5,
          rowGap: 1,
          alignItems: 'start',
        }}
      >
        <NodeSearchField label="Source *" defaultValue={autoSource} onChange={setSource} />
        <NodeSearchField
          label="Destination *"
          defaultValue={initialDestination}
          defaultInputValue={initialDestinationInputValue}
          suggestions={destinationSuggestions}
          onChange={setDestination}
        />
        <ShipmentReferenceFields
          note={note}
          ref1={ref1}
          onNoteChange={setNote}
          onRef1Change={setRef1}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mt: 1, fontSize: '11px', color: 'text.secondary' }}>
        <span>
          Client/Contract:{' '}
          <strong>
            {clientName ?? '—'} → {contractName ?? '—'}
          </strong>
        </span>
        <span>
          Service: <strong>JOB-{serviceId}</strong>
        </span>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={bypassReceipt}
            onChange={(e) => setBypassReceipt(e.target.checked)}
          />
        }
        label="Bypass Receipt"
        slotProps={{ typography: { sx: { fontSize: '12px' } } }}
        sx={{ mt: 0.5 }}
      />

      <ManifestSection
        manifest={manifestState.manifest}
        serialised={manifestState.serialised}
        nonSerialised={manifestState.nonSerialised}
        partSearchInput={manifestState.partSearchInput}
        partOptions={manifestState.partOptions}
        partSearchPending={manifestState.partSearchPending}
        conditionChoice={manifestState.conditionChoice}
        notice={manifestState.notice}
        stockResolvePending={manifestState.stockResolvePending}
        onPartSearchInputChange={manifestState.setPartSearchInput}
        onAddItem={manifestState.addManifestItem}
        onChooseCondition={manifestState.chooseCondition}
        onDismissConditionChoice={manifestState.dismissConditionChoice}
        onRemoveItem={manifestState.removeItem}
        onUpdateQty={manifestState.updateQty}
        onExport={manifestState.exportManifest}
      />

      {submitState.error && (
        <Typography sx={{ fontSize: '11px', color: 'error.main', mt: 1 }}>
          {submitState.error}
        </Typography>
      )}

      <ShipmentFormActions
        saving={submitState.saving}
        canDispatch={canDispatch}
        pendingAction={submitState.pendingAction}
        submitDisabled={manifestState.manifest.length === 0}
        onCancel={onCancel}
        onSubmit={() => submitState.handleSubmit('link')}
        onSubmitWithConnote={() => submitState.handleSubmit('connote')}
      />
    </Box>
  );
};
